import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { endOfDay } from 'date-fns';
import { Subject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { parseDateOrDefault } from 'src/util/dateLib';
import {
  areEqual,
  DateRange,
  DateRangeChangeEvent,
  DateRangeOption,
  getDateRangeOptions,
} from './date-range-picker.model';

@Component({
  selector: 'fl-date-range-picker',
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateRangePickerComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  public rangeKey: string | null = null;
  @Input()
  public startDate: Date | string | null = null;
  @Input()
  public endDate: Date | string | null = null;

  // tslint:disable-next-line:no-output-on-prefix
  @Output()
  public onChange = new EventEmitter<DateRangeChangeEvent>();
  // tslint:disable-next-line:no-output-on-prefix
  @Output()
  public onInit = new EventEmitter<DateRangeChangeEvent>();

  public dateRangeForm: FormGroup;
  public options: DateRangeOption[] = getDateRangeOptions();

  public _onDestroy$ = new Subject();
  public range$: Subject<DateRange> = new Subject<DateRange>();

  public getRangeFromInput(): DateRange {
    const startDate = parseDateOrDefault(this.startDate);
    const endDate = parseDateOrDefault(this.endDate);

    if (startDate == null && endDate == null) {
      const option = this.options.find(
        // tslint:disable-next-line: no-non-null-assertion
        (opt) => opt.key.toLowerCase() === this.rangeKey!.toLowerCase(),
      );
      if (option != null) {
        return option.value;
      }
    }
    return {
      startDate,
      endDate,
    };
  }

  constructor(private fb: FormBuilder) {}
  public ngOnChanges() {
    // TODO: Validate inputs and throw console warning/error for development purposes
    const inputRange = this.getRangeFromInput();
    this.range$.next(inputRange);
  }

  public createChangeEvent(
    range: DateRange,
    option: DateRangeOption | undefined,
  ): DateRangeChangeEvent {
    return {
      rangeKey: option != null ? option.key : null,
      range,
    };
  }

  public ngOnInit() {
    // Get initial input values and emit an onInit event to notify
    // parent components of initial values
    const inputRange = this.getRangeFromInput();
    const rangeOption = this.findMatchingOption(inputRange);
    this.onInit.emit(this.createChangeEvent(inputRange, rangeOption));

    // Build the date range form
    this.dateRangeForm = this.fb.group({
      rangeSelect: rangeOption != null ? this.fb.control(rangeOption.value) : null,
      range: this.fb.group({
        // tslint:disable-next-line: no-non-null-assertion
        startDate: this.fb.control(inputRange.startDate!),
        // tslint:disable-next-line: no-non-null-assertion
        endDate: this.fb.control(inputRange.endDate!),
      }),
    });

    // For new
    this.range$
      .pipe(distinctUntilChanged(areEqual), takeUntil(this._onDestroy$))
      .subscribe((range) => {
        if (range != null) {
          const option = this.findMatchingOption(range);
          this.dateRangeForm.patchValue(
            {
              rangeSelect: option != null ? option.value : null,
              range,
            },
            { emitEvent: false },
          );
          const onChangeEvent = this.createChangeEvent(range, option);
          this.onChange.emit(onChangeEvent);
        }
      });

    // tslint:disable-next-line: no-non-null-assertion
    this.dateRangeForm
      .get('rangeSelect')!
      .valueChanges.pipe(distinctUntilChanged(areEqual), takeUntil(this._onDestroy$))
      .subscribe((range) => {
        this.range$.next(range);
      });

    // tslint:disable-next-line: no-non-null-assertion
    this.dateRangeForm
      .get('range')!
      .valueChanges.pipe(
        map((range) => {
          return {
            startDate: range.startDate,
            endDate: range.endDate != null ? endOfDay(range.endDate) : undefined,
          };
        }),
        distinctUntilChanged(areEqual),
        takeUntil(this._onDestroy$),
      )
      .subscribe((range) => {
        this.range$.next(range);
      });
  }

  public ngOnDestroy() {
    this._onDestroy$.next();
  }

  public findMatchingOption(range: DateRange): DateRangeOption | undefined {
    const match = this.options.find((o) => areEqual(range, o.value));
    return match;
  }
}
